import {
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Spin,
  Statistic,
  Table,
  Tag,
  message,
} from 'antd';
import { useRef, useState } from 'react';
import { useRequest } from 'ahooks';
import {
  CompanyDTO,
  CustomerPoolEnum,
  CustomerStateEnum,
  SmsConfig,
  smsController,
  systemController,
} from '../../api';
import { useCompany } from '../../hooks/company';
import { PageHeader } from '@ant-design/pro-components';
import {
  ClearOutlined,
  ReloadOutlined,
  SendOutlined,
  SettingOutlined,
} from '@ant-design/icons';
import {
  customerStateMapping,
  customerPoolMapping,
} from '../../constants';
import {
  getSmsSendRecords,
  clearSmsSendRecords,
  SmsSendRecordDTO,
} from '../../apis/api';

const starOptions = [0, 1, 2, 3, 4, 5].map((v) => ({
  label: `${v} 星`,
  value: v,
}));

const splitCustomerIds = (raw: string) =>
  raw
    .split(/[,，\s\n]+/)
    .map((item) => item.trim())
    .filter(Boolean);

const SmsConfigEditor = () => {
  const [form] = Form.useForm<SmsConfig>();
  const latestConfigRef = useRef<SmsConfig>({});

  const { loading: getLoading, refresh } = useRequest(
    () => smsController.getConfig(undefined as any),
    {
      onSuccess: (data) => {
        latestConfigRef.current = data || {};
        form.setFieldsValue(data || {});
      },
    },
  );

  const { loading: updateLoading, run: update } = useRequest(
    (values: SmsConfig) => smsController.updateConfig(undefined as any, values),
    {
      manual: true,
      onSuccess: () => {
        message.success('配置已保存');
        refresh();
      },
      onError: (e) => message.error(e.message || '保存失败'),
    },
  );

  const handleSave = () => {
    form.validateFields().then((values) => {
      if (!values.content && !values.templateId) {
        message.error('短信内容和模板 ID 至少填写一个');
        return;
      }
      if (values.content && values.templateId) {
        message.error('短信内容和模板 ID 只能填写一个');
        return;
      }
      update({
        ...latestConfigRef.current,
        ...values,
      });
    });
  };

  return (
    <Spin spinning={getLoading}>
      <Form form={form} layout="vertical">
        <Form.Item
          label="网关地址"
          name="gatewayUrl"
          rules={[{ required: true, message: '请输入网关地址' }]}
        >
          <Input placeholder="http://host:port" />
        </Form.Item>
        <Form.Item
          label="账号"
          name="userName"
          rules={[{ required: true, message: '请输入账号' }]}
        >
          <Input placeholder="SMS 网关账号" />
        </Form.Item>
        <Form.Item
          label="密码"
          name="password"
          rules={[{ required: true, message: '请输入密码' }]}
        >
          <Input.Password placeholder="SMS 网关密码" />
        </Form.Item>
        <Form.Item label="短信内容" name="content">
          <Input.TextArea
            rows={3}
            placeholder="短信内容（与模板 ID 二选一）"
          />
        </Form.Item>
        <Form.Item label="模板 ID" name="templateId">
          <InputNumber
            placeholder="短信模板 ID（与内容二选一）"
            style={{ width: '100%' }}
          />
        </Form.Item>
        <Button
          type="primary"
          onClick={handleSave}
          loading={updateLoading}
          icon={<SettingOutlined />}
        >
          保存配置
        </Button>
      </Form>
    </Spin>
  );
};

const SmsBalance = () => {
  const {
    data: balance,
    loading,
    refresh,
  } = useRequest(() => smsController.getBalance(undefined as any), {
    onError: (e) => message.error(e.message || '查询余额失败'),
  });

  return (
    <Space direction="vertical" style={{ width: '100%' }}>
      <Statistic
        title="短信余额"
        value={balance?.balance ?? '-'}
        suffix="条"
        loading={loading}
      />
      {balance?.code !== undefined && balance.code !== 0 && (
        <Descriptions size="small">
          <Descriptions.Item label="状态码">{balance.code}</Descriptions.Item>
          <Descriptions.Item label="消息">{balance.message}</Descriptions.Item>
        </Descriptions>
      )}
      <Button icon={<ReloadOutlined />} onClick={refresh} loading={loading}>
        刷新
      </Button>
    </Space>
  );
};

const SendByIds = ({ companies }: { companies?: CompanyDTO[] }) => {
  const [form] = Form.useForm();

  const { loading, run: send } = useRequest(
    (values: { companyId: number; customerIds: string }) =>
      smsController.sendByIds({
        companyId: values.companyId,
        customerIds: splitCustomerIds(values.customerIds),
      }),
    {
      manual: true,
      onSuccess: (data) => {
        if (data?.code === 0) {
          message.success(
            `发送成功，消耗 ${data.smsCount} 条短信`,
          );
          form.resetFields(['customerIds']);
        } else {
          message.error(data?.message || '发送失败');
        }
      },
      onError: (e) => message.error(e.message || '发送失败'),
    },
  );

  const handleSend = () => {
    form.validateFields().then((values) => {
      Modal.confirm({
        title: '确认发送',
        content: '确定要向这些客户发送短信吗？',
        onOk: () => send(values),
      });
    });
  };

  return (
    <Form form={form} layout="vertical">
      <Form.Item
        label="公司"
        name="companyId"
        rules={[{ required: true, message: '请选择公司' }]}
      >
        <Select
          placeholder="选择公司"
          options={companies?.map((c) => ({ label: c.name, value: c.id }))}
          showSearch
          filterOption={(input, option) =>
            (option?.label as string)?.includes(input)
          }
        />
      </Form.Item>
      <Form.Item
        label="客户 ID 列表"
        name="customerIds"
        rules={[
          { required: true, message: '请输入客户 ID' },
          {
            validator: async (_, value?: string) => {
              const ids = splitCustomerIds(value || '');
              if (!ids.length) {
                throw new Error('请输入客户 ID');
              }
              if (ids.some((id) => !/^\d+$/.test(id))) {
                throw new Error('客户 ID 仅支持整数，用逗号、空格或换行分隔');
              }
            },
          },
        ]}
      >
        <Input.TextArea
          rows={4}
          placeholder="输入客户 ID，用逗号、空格或换行分隔"
        />
      </Form.Item>
      <Button
        type="primary"
        onClick={handleSend}
        loading={loading}
        icon={<SendOutlined />}
      >
        发送短信
      </Button>
    </Form>
  );
};

interface FilterFormValues {
  companyId: number;
  star?: number[];
  pool?: CustomerPoolEnum[];
  state?: CustomerStateEnum[];
  createdAtRange?: [import('dayjs').Dayjs, import('dayjs').Dayjs];
}

const SendByFilter = ({ companies }: { companies?: CompanyDTO[] }) => {
  const { systemConfig } = useCompany();
  const [form] = Form.useForm<FilterFormValues>();

  // 使用 systemConfig 中过滤后的客户状态和客户池
  const stateOptions = (systemConfig?.customerStates || []).map((s) => ({
    label: customerStateMapping[s] || s,
    value: s,
  }));

  const poolOptions = Object.entries(customerPoolMapping).map(
    ([value, label]) => ({ label, value }),
  );

  const { loading, run: send } = useRequest(
    (values: FilterFormValues) =>
      smsController.sendByFilter({
        companyId: values.companyId,
        star: values.star,
        pool: values.pool,
        state: values.state,
        createdAtStart: values.createdAtRange?.[0]?.format(
          'YYYY-MM-DDTHH:mm:ss',
        ),
        createdAtEnd: values.createdAtRange?.[1]?.format(
          'YYYY-MM-DDTHH:mm:ss',
        ),
      }),
    {
      manual: true,
      onSuccess: () => {
        message.success('批量发送任务已提交，后台异步执行中');
      },
      onError: (e) => message.error(e.message || '发送失败'),
    },
  );

  const handleSend = () => {
    form.validateFields().then((values) => {
      if (
        !values.star?.length &&
        !values.pool?.length &&
        !values.state?.length &&
        !values.createdAtRange?.length
      ) {
        message.error('请至少选择一个筛选条件');
        return;
      }

      Modal.confirm({
        title: '确认批量发送',
        content: '确定要按筛选条件批量发送短信吗？此操作将异步执行。',
        onOk: () => send(values),
      });
    });
  };

  return (
    <Form form={form} layout="vertical">
      <Form.Item
        label="公司"
        name="companyId"
        rules={[{ required: true, message: '请选择公司' }]}
      >
        <Select
          placeholder="选择公司"
          options={companies?.map((c) => ({ label: c.name, value: c.id }))}
          showSearch
          filterOption={(input, option) =>
            (option?.label as string)?.includes(input)
          }
        />
      </Form.Item>
      <Form.Item label="星级" name="star">
        <Select
          mode="multiple"
          placeholder="选择星级（可多选）"
          options={starOptions}
          allowClear
        />
      </Form.Item>
      <Form.Item label="客户池" name="pool">
        <Select
          mode="multiple"
          placeholder="选择客户池（可多选）"
          options={poolOptions}
          allowClear
        />
      </Form.Item>
      <Form.Item label="客户状态" name="state">
        <Select
          mode="multiple"
          placeholder="选择状态（可多选）"
          options={stateOptions}
          allowClear
        />
      </Form.Item>
      <Form.Item label="创建时间范围" name="createdAtRange">
        <DatePicker.RangePicker
          showTime
          style={{ width: '100%' }}
          placeholder={['开始时间', '结束时间']}
        />
      </Form.Item>
      <Button
        type="primary"
        onClick={handleSend}
        loading={loading}
        icon={<SendOutlined />}
      >
        批量发送
      </Button>
    </Form>
  );
};

const statusMap: Record<number, { text: string; color: string }> = {
  0: { text: '待发送', color: 'blue' },
  1: { text: '成功', color: 'green' },
  2: { text: '失败', color: 'red' },
};

const SendRecords = ({ companies }: { companies?: CompanyDTO[] }) => {
  const [companyId, setCompanyId] = useState<number>();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20 });

  const {
    data,
    loading,
    run: fetchRecords,
    refresh,
  } = useRequest(
    (params?: { current?: number; pageSize?: number; companyId?: number }) => {
      const cid = params?.companyId ?? companyId;
      if (!cid) {
        return Promise.resolve({ total: 0, size: 20, page: 0, list: [] as SmsSendRecordDTO[] });
      }
      const cur = params?.current ?? pagination.current;
      const size = params?.pageSize ?? pagination.pageSize;
      return getSmsSendRecords(cid, cur, size);
    },
    {
      manual: true,
    },
  );

  const { loading: clearing, run: handleClear } = useRequest(
    () => clearSmsSendRecords(companyId!),
    {
      manual: true,
      onSuccess: () => {
        message.success('发送记录已清空');
        setPagination((prev) => ({ ...prev, current: 1 }));
        fetchRecords({ current: 1 });
      },
      onError: (e) => message.error(e.message || '清空失败'),
    },
  );

  const handleCompanyChange = (value: number) => {
    setCompanyId(value);
    setPagination({ current: 1, pageSize: 20 });
    fetchRecords({ current: 1, pageSize: 20, companyId: value });
  };

  const columns = [
    {
      title: '手机号',
      dataIndex: 'mobile',
      key: 'mobile',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: number) => {
        const info = statusMap[status] || { text: `未知(${status})`, color: 'default' };
        return <Tag color={info.color}>{info.text}</Tag>;
      },
    },
    {
      title: '错误信息',
      dataIndex: 'errorMessage',
      key: 'errorMessage',
      render: (text: string | null) => text || '-',
    },
    {
      title: '发送时间',
      dataIndex: 'createdAt',
      key: 'createdAt',
    },
  ];

  return (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <Space>
        <Select
          placeholder="选择公司"
          style={{ width: 200 }}
          options={companies?.map((c) => ({ label: c.name, value: c.id }))}
          showSearch
          filterOption={(input, option) =>
            (option?.label as string)?.includes(input)
          }
          onChange={handleCompanyChange}
          value={companyId}
        />
        <Button
          icon={<ReloadOutlined />}
          onClick={() => refresh()}
          disabled={!companyId}
          loading={loading}
        >
          刷新
        </Button>
        <Button
          danger
          icon={<ClearOutlined />}
          disabled={!companyId}
          loading={clearing}
          onClick={() => {
            Modal.confirm({
              title: '确认清空',
              content: '确定要清空该公司的所有发送记录吗？此操作不可恢复。',
              okType: 'danger',
              onOk: () => handleClear(),
            });
          }}
        >
          一键清空
        </Button>
      </Space>
      <Table<SmsSendRecordDTO>
        rowKey="id"
        columns={columns}
        dataSource={data?.list}
        loading={loading}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: data?.total,
          showSizeChanger: true,
          showTotal: (total) => `共 ${total} 条`,
          onChange: (page, pageSize) => {
            setPagination({ current: page, pageSize });
            fetchRecords({ current: page, pageSize });
          },
        }}
      />
    </Space>
  );
};

export default () => {
  const { currentUser } = useCompany();
  const isSuperAdmin = !!currentUser?.isSuperAdmin;

  const { data: companies } = useRequest(
    () => systemController.listCompany(),
    { ready: isSuperAdmin },
  );

  if (!isSuperAdmin) {
    return null;
  }

  return (
    <PageHeader title="短信群发">
      <Row gutter={[16, 16]}>
        <Col span={12}>
          <Card title="短信配置" size="small">
            <SmsConfigEditor />
          </Card>
        </Col>
        <Col span={12}>
          <Card title="短信余额" size="small">
            <SmsBalance />
          </Card>
        </Col>
        <Col span={12}>
          <Card title="按客户 ID 发送" size="small">
            <SendByIds companies={companies} />
          </Card>
        </Col>
        <Col span={12}>
          <Card title="按条件批量发送" size="small">
            <SendByFilter companies={companies} />
          </Card>
        </Col>
        <Col span={24}>
          <Card title="发送记录" size="small">
            <SendRecords companies={companies} />
          </Card>
        </Col>
      </Row>
    </PageHeader>
  );
};
